/**
 * Room Booking Popup Component
 * Handles compact modal lifecycle, schedule calendar, occupancy/variant switching,
 * intl-tel-input phone validation, simultaneous Supabase submission,
 * and clean Shopify Cart & Checkout redirect.
 * Global Trigger: Any element with class .js-room-booking-trigger
 */
(function() {
  'use strict';

  var MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  class RoomBookingPopup extends HTMLElement {
    constructor() {
      super();

      this.overlay = this.querySelector('[data-popup-overlay]');
      this.closeBtn = this.querySelector('[data-popup-close]');
      this.form = this.querySelector('[data-booking-form]');
      this.submitBtn = this.querySelector('[data-submit-btn]');
      this.submitText = this.querySelector('.room-booking-popup__submit-text');
      this.submitSpinner = this.querySelector('.room-booking-popup__submit-spinner');
      this.summaryPrice = this.querySelector('[data-summary-price]');
      this.messageEl = this.querySelector('[data-popup-message]');

      // Form inputs
      this.nameInput = this.querySelector('input[name="guest_name"]');
      this.phoneInput = this.querySelector('input[name="guest_phone"]');
      this.emailInput = this.querySelector('input[name="guest_email"]');
      this.segments = this.querySelectorAll('.room-booking-popup__segment');

      // Calendar elements
      this.calPrevBtn = this.querySelector('[data-cal-prev]');
      this.calNextBtn = this.querySelector('[data-cal-next]');
      this.calMonthLabel = this.querySelector('[data-cal-month-label]');
      this.calDaysGrid = this.querySelector('[data-cal-days-grid]');
      this.selectedDateInput = this.querySelector('[data-selected-date-input]');
      this.checkInInput = this.querySelector('[data-checkin-input]');
      this.checkOutInput = this.querySelector('[data-checkout-input]');
      this.chipStart = this.querySelector('[data-chip-step="start"]');
      this.chipEnd = this.querySelector('[data-chip-step="end"]');
      this.chipStartVal = this.querySelector('[data-chip-start-val]');
      this.chipEndVal = this.querySelector('[data-chip-end-val]');
      this.nightsTag = this.querySelector('[data-nights-tag]');

      // Supabase settings
      this.supabaseUrl = this.dataset.supabaseUrl || '';
      this.supabaseKey = this.dataset.supabaseKey || '';
      this.supabaseTable = this.dataset.supabaseTable || 'bookings';

      // Phone instance
      this.iti = null;
      this.previousPhoneValue = '';

      // Calendar range state
      var today = new Date();
      this.currentYear = today.getFullYear();
      this.currentMonth = today.getMonth();
      this.startDate = null;
      this.endDate = null;
      this.hoverDate = null;
      this.selectingStep = 'start';

      // Mock booked dates
      this.mockBookedDates = this.generateMockBookedDates();

      // Bound methods
      this.open = this.open.bind(this);
      this.close = this.close.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      this.initEvents();
      this.initCalendar();
      this.initIntlTelInput();
      this.resolveFallbackVariantId();
    }

    initIntlTelInput() {
      var self = this;
      var init = function() {
        if (self.phoneInput && window.intlTelInput) {
          var defaultCountry = (self.dataset.defaultCountry || 'in').toLowerCase();
          self.iti = window.intlTelInput(self.phoneInput, {
            initialCountry: defaultCountry,
            preferredCountries: ['in', 'us', 'de'],
            separateDialCode: true,
            dropdownContainer: self.querySelector('.room-booking-popup__phone-group') || document.body
          });
          self.updatePhoneMaxLength();
          self.bindPhoneValidation();
          return true;
        }
        return false;
      };

      if (!init()) {
        var interval = setInterval(function() {
          if (init()) clearInterval(interval);
        }, 100);
      }
    }

    updatePhoneMaxLength() {
      if (this.iti && this.phoneInput) {
        var countryData = this.iti.getSelectedCountryData();
        var rules = {
          in: { max: 10 },
          us: { max: 10 },
          de: { max: 11 },
          gb: { max: 10 },
          ae: { max: 9 },
          au: { max: 9 },
          fr: { max: 9 },
          ch: { max: 9 },
          za: { max: 9 },
          nl: { max: 9 },
          jp: { max: 10 }
        };
        if (rules[countryData.iso2]) {
          this.phoneInput.maxLength = rules[countryData.iso2].max;
        } else {
          this.phoneInput.maxLength = 15;
        }
      }
    }

    bindPhoneValidation() {
      var self = this;
      if (!this.phoneInput) return;

      this.phoneInput.addEventListener('input', function(e) {
        var validVal = e.target.value.replace(/[^\d\s\-\(\)\+]/g, '');
        if (e.target.value !== validVal) {
          e.target.value = validVal;
        }

        if (window.intlTelInputUtils && self.iti) {
          var countryData = self.iti.getSelectedCountryData();
          var iso = countryData.iso2;
          var cleanDigits = e.target.value.replace(/\D/g, '');

          var startRules = {
            in: /^[6789]/,
            us: /^[2-9]/,
            de: /^[1]/,
            gb: /^[7]/,
            ae: /^[5]/,
            au: /^[4]/,
            fr: /^[67]/,
            ch: /^[7]/,
            za: /^[678]/,
            nl: /^[6]/,
            jp: /^[789]/
          };

          if (startRules[iso] && cleanDigits.length > 0) {
            if (cleanDigits.startsWith('0')) {
              e.target.value = e.target.value.replace(/^[0\s\-\(\)]+/, '');
              cleanDigits = e.target.value.replace(/\D/g, '');
            }

            if (cleanDigits.length > 0 && !startRules[iso].test(cleanDigits)) {
              e.target.value = '';
            }
          }

          var err = self.iti.getValidationError();
          if (
            !self.iti.isValidNumber() &&
            (err === window.intlTelInputUtils.validationError.TOO_LONG ||
              err === window.intlTelInputUtils.validationError.INVALID_LENGTH)
          ) {
            if (e.inputType === 'insertText') {
              e.target.value = self.previousPhoneValue;
            }
          } else {
            self.previousPhoneValue = e.target.value;
          }
        }
      });

      this.phoneInput.addEventListener('blur', function() {
        self.validatePhone(true);
      });

      this.phoneInput.addEventListener('countrychange', function() {
        self.previousPhoneValue = self.phoneInput.value;
        self.updatePhoneMaxLength();
        self.validatePhone(false);
      });
    }

    validatePhone(showError) {
      if (!this.phoneInput) return true;
      var phone = this.phoneInput.value.trim();
      var phoneErrorEl = this.form.querySelector('[data-error="guest_phone"]');

      var self = this;
      var setError = function(msg) {
        if (showError && phoneErrorEl) {
          if (msg) {
            phoneErrorEl.textContent = msg;
            phoneErrorEl.classList.add('is-visible');
            self.phoneInput.style.borderColor = '#d32f2f';
          } else {
            phoneErrorEl.textContent = '';
            phoneErrorEl.classList.remove('is-visible');
            self.phoneInput.style.borderColor = '';
          }
        }
      };

      if (phone === '') {
        setError('Please enter a valid phone number');
        return false;
      }

      var cleanPhone = phone.replace(/\D/g, '');

      if (cleanPhone.length > 5 && /^(\d)\1+$/.test(cleanPhone)) {
        setError('Repeating digits are not allowed');
        return false;
      }

      if (this.iti) {
        var countryData = this.iti.getSelectedCountryData();
        var iso = countryData.iso2;

        var startRules = {
          in: /^[6789]/,
          us: /^[2-9]/,
          de: /^[1]/,
          gb: /^[7]/,
          ae: /^[5]/,
          au: /^[4]/,
          fr: /^[67]/,
          ch: /^[7]/,
          za: /^[678]/,
          nl: /^[6]/,
          jp: /^[789]/
        };

        if (startRules[iso] && cleanPhone.length > 0) {
          if (!startRules[iso].test(cleanPhone)) {
            setError('Should start with valid prefix for ' + countryData.name);
            return false;
          }
        }

        if (!this.iti.isValidNumber()) {
          var errorMsg = 'Please enter a valid phone number';
          if (window.intlTelInputUtils) {
            var errorCode = this.iti.getValidationError();
            switch (errorCode) {
              case window.intlTelInputUtils.validationError.TOO_SHORT:
                errorMsg = 'Phone number is too short';
                break;
              case window.intlTelInputUtils.validationError.TOO_LONG:
                errorMsg = 'Phone number is too long';
                break;
              case window.intlTelInputUtils.validationError.INVALID_COUNTRY_CODE:
                errorMsg = 'Invalid country code';
                break;
              default:
                errorMsg = 'Invalid phone number for selected country';
            }
          }
          setError(errorMsg);
          return false;
        }
      }

      setError('');
      return true;
    }

    getVariant1() {
      return this.getAttribute('data-variant-1') ||
        this.getAttribute('data-variant1') ||
        (this.dataset ? (this.dataset['variant-1'] || this.dataset.variant1) : '') ||
        '';
    }

    getVariant2() {
      return this.getAttribute('data-variant-2') ||
        this.getAttribute('data-variant2') ||
        (this.dataset ? (this.dataset['variant-2'] || this.dataset.variant2) : '') ||
        '';
    }

    getPrice1() {
      return this.getAttribute('data-price-1') ||
        this.getAttribute('data-price1') ||
        (this.dataset ? (this.dataset['price-1'] || this.dataset.price1) : '') ||
        'Rs. 50,000.00';
    }

    getPrice2() {
      return this.getAttribute('data-price-2') ||
        this.getAttribute('data-price2') ||
        (this.dataset ? (this.dataset['price-2'] || this.dataset.price2) : '') ||
        'Rs. 80,000.00';
    }

    resolveFallbackVariantId() {
      var self = this;
      var v1 = this.getVariant1();
      var v2 = this.getVariant2();

      if (!v1 || isNaN(parseInt(v1, 10)) || !v2 || isNaN(parseInt(v2, 10))) {
        fetch('/products.json?limit=10')
          .then(function(r) { return r.json(); })
          .then(function(data) {
            if (data && data.products && data.products.length > 0) {
              var singleProd = data.products.find(function(p) {
                var handle = (p.handle || '').toLowerCase();
                var title = (p.title || '').toLowerCase();
                return handle.indexOf('single') !== -1 || title.indexOf('single') !== -1 || handle.indexOf('1') !== -1;
              });

              var doubleProd = data.products.find(function(p) {
                var handle = (p.handle || '').toLowerCase();
                var title = (p.title || '').toLowerCase();
                return handle.indexOf('double') !== -1 || title.indexOf('double') !== -1 || handle.indexOf('2') !== -1;
              });

              if (!singleProd || !doubleProd) {
                var sorted = data.products.slice().sort(function(a, b) {
                  var pa = a.variants && a.variants[0] ? parseFloat(a.variants[0].price) : 0;
                  var pb = b.variants && b.variants[0] ? parseFloat(b.variants[0].price) : 0;
                  return pa - pb;
                });
                singleProd = singleProd || sorted[0];
                doubleProd = doubleProd || (sorted.length > 1 ? sorted[1] : sorted[0]);
              }

              if (singleProd && singleProd.variants && singleProd.variants.length > 0) {
                var sVar = singleProd.variants[0];
                self.setAttribute('data-variant-1', String(sVar.id));
                self.setAttribute('data-variant1', String(sVar.id));
                if (sVar.price) {
                  var pStr1 = 'Rs. ' + parseFloat(sVar.price).toLocaleString('en-IN', { minimumFractionDigits: 2 });
                  self.setAttribute('data-price-1', pStr1);
                  self.setAttribute('data-price1', pStr1);
                  var tag1 = self.querySelector('[data-price-tag-1]');
                  if (tag1) tag1.textContent = pStr1;
                }
              }

              if (doubleProd && doubleProd.variants && doubleProd.variants.length > 0) {
                var dVar = (doubleProd === singleProd && doubleProd.variants.length > 1)
                  ? doubleProd.variants[1]
                  : doubleProd.variants[0];
                self.setAttribute('data-variant-2', String(dVar.id));
                self.setAttribute('data-variant2', String(dVar.id));
                if (dVar.price) {
                  var pStr2 = 'Rs. ' + parseFloat(dVar.price).toLocaleString('en-IN', { minimumFractionDigits: 2 });
                  self.setAttribute('data-price-2', pStr2);
                  self.setAttribute('data-price2', pStr2);
                  var tag2 = self.querySelector('[data-price-tag-2]');
                  if (tag2) tag2.textContent = pStr2;
                }
              }

              self.updateOccupancyUI();
            }
          })
          .catch(function() {});
      }
    }

    generateMockBookedDates() {
      var dates = [];
      var now = new Date();
      var offsets = [2, 3, 5, 8, 9, 14, 15, 22, 23, 29, 30, 35, 42];
      for (var i = 0; i < offsets.length; i++) {
        var d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offsets[i]);
        dates.push(this.formatDate(d));
      }
      return dates;
    }

    formatDate(d) {
      var year = d.getFullYear();
      var month = String(d.getMonth() + 1).padStart(2, '0');
      var day = String(d.getDate()).padStart(2, '0');
      return year + '-' + month + '-' + day;
    }

    formatDisplayDate(dateStr) {
      if (!dateStr) return '';
      var parts = dateStr.split('-');
      if (parts.length !== 3) return dateStr;
      var d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    }

    formatShortDate(dateStr) {
      if (!dateStr) return 'Select Date';
      var parts = dateStr.split('-');
      if (parts.length !== 3) return dateStr;
      var d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }

    getNightsCount() {
      if (!this.startDate || !this.endDate) return 0;
      var d1 = new Date(this.startDate);
      var d2 = new Date(this.endDate);
      return Math.max(1, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)));
    }

    initEvents() {
      var self = this;

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this.close);
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', this.close);
      }

      document.addEventListener('keydown', this.onKeyDown);

      // Occupancy segment radio change listener
      this.form.querySelectorAll('input[name="occupancy_type"]').forEach(function(radio) {
        radio.addEventListener('change', function() {
          self.updateOccupancyUI();
        });
      });

      // Segment container clicks
      this.segments.forEach(function(segment) {
        segment.addEventListener('click', function(e) {
          var radio = segment.querySelector('input[type="radio"]');
          if (radio && !radio.checked) {
            radio.checked = true;
            self.updateOccupancyUI();
          }
        });
      });

      // Date Range Chips
      if (this.chipStart) {
        this.chipStart.addEventListener('click', function() {
          self.selectingStep = 'start';
          self.updateDateRangeUI();
        });
      }

      if (this.chipEnd) {
        this.chipEnd.addEventListener('click', function() {
          if (self.startDate) {
            self.selectingStep = 'end';
            self.updateDateRangeUI();
          }
        });
      }

      // Hover on calendar days grid
      if (this.calDaysGrid) {
        this.calDaysGrid.addEventListener('mouseleave', function() {
          if (self.startDate && !self.endDate) {
            self.hoverDate = null;
            self.renderCalendar();
          }
        });
      }

      // Calendar month buttons
      if (this.calPrevBtn) {
        this.calPrevBtn.addEventListener('click', function() {
          self.currentMonth--;
          if (self.currentMonth < 0) {
            self.currentMonth = 11;
            self.currentYear--;
          }
          self.renderCalendar();
        });
      }

      if (this.calNextBtn) {
        this.calNextBtn.addEventListener('click', function() {
          self.currentMonth++;
          if (self.currentMonth > 11) {
            self.currentMonth = 0;
            self.currentYear++;
          }
          self.renderCalendar();
        });
      }

      // Form submission
      if (this.form) {
        this.form.addEventListener('submit', this.handleFormSubmit);
      }

      // Input error clearing
      this.form.querySelectorAll('input').forEach(function(input) {
        input.addEventListener('input', function() {
          var err = self.form.querySelector('[data-error="' + input.name + '"]');
          if (err) {
            err.textContent = '';
            err.classList.remove('is-visible');
          }
        });
      });
    }

    initCalendar() {
      this.updateDateRangeUI();
      this.renderCalendar();
    }

    clearDates() {
      this.startDate = null;
      this.endDate = null;
      this.hoverDate = null;
      this.selectingStep = 'start';
      this.updateDateRangeUI();
      this.renderCalendar();
    }

    updateDateRangeUI() {
      if (this.chipStart) {
        this.chipStart.classList.toggle('is-active', this.selectingStep === 'start');
        this.chipStart.classList.toggle('has-date', !!this.startDate);
      }
      if (this.chipEnd) {
        this.chipEnd.classList.toggle('is-active', this.selectingStep === 'end');
        this.chipEnd.classList.toggle('has-date', !!this.endDate);
      }

      if (this.chipStartVal) {
        this.chipStartVal.textContent = this.startDate ? this.formatShortDate(this.startDate) : 'Select Date';
      }
      if (this.chipEndVal) {
        this.chipEndVal.textContent = this.endDate ? this.formatShortDate(this.endDate) : 'Select Date';
      }

      var nights = this.getNightsCount();
      if (this.nightsTag) {
        if (this.startDate && this.endDate && nights > 0) {
          this.nightsTag.textContent = nights + (nights === 1 ? ' Night' : ' Nights');
          this.nightsTag.style.display = 'inline-block';
        } else {
          this.nightsTag.style.display = 'none';
        }
      }

      if (this.checkInInput) {
        this.checkInInput.value = this.startDate || '';
      }
      if (this.checkOutInput) {
        this.checkOutInput.value = this.endDate || '';
      }
      if (this.selectedDateInput) {
        this.selectedDateInput.value = (this.startDate && this.endDate)
          ? (this.startDate + ' to ' + this.endDate)
          : (this.startDate || '');
      }

      var err = this.form ? this.form.querySelector('[data-error="schedule_date"]') : null;
      if (err && this.startDate && this.endDate) {
        err.textContent = '';
        err.classList.remove('is-visible');
      }
    }

    rangeContainsBookedDate(startStr, endStr) {
      if (!this.mockBookedDates || !this.mockBookedDates.length) return false;
      for (var i = 0; i < this.mockBookedDates.length; i++) {
        var b = this.mockBookedDates[i];
        if (b >= startStr && b <= endStr) {
          return true;
        }
      }
      return false;
    }

    highlightHoverRange() {
      if (!this.startDate || this.endDate || !this.hoverDate) return;
      var self = this;
      var allDays = this.calDaysGrid.querySelectorAll('.room-booking-popup__cal-day[data-date]');
      allDays.forEach(function(day) {
        var dt = day.dataset.date;
        if (day.classList.contains('is-booked') || day.classList.contains('is-disabled')) return;
        if (dt === self.startDate) return;

        day.classList.remove('is-hover-range', 'is-hover-end');

        if (dt > self.startDate && dt < self.hoverDate) {
          day.classList.add('is-hover-range');
        } else if (dt === self.hoverDate) {
          day.classList.add('is-hover-end');
        }
      });
    }

    renderCalendar() {
      if (!this.calDaysGrid || !this.calMonthLabel) return;

      this.calMonthLabel.textContent = MONTH_NAMES[this.currentMonth] + ' ' + this.currentYear;

      var today = new Date();
      today.setHours(0, 0, 0, 0);

      var isCurrentMonth = (this.currentYear === today.getFullYear() && this.currentMonth === today.getMonth());
      if (this.calPrevBtn) {
        this.calPrevBtn.disabled = isCurrentMonth;
        this.calPrevBtn.style.opacity = isCurrentMonth ? '0.3' : '1';
        this.calPrevBtn.style.pointerEvents = isCurrentMonth ? 'none' : 'auto';
      }

      var firstDayIndex = new Date(this.currentYear, this.currentMonth, 1).getDay();
      var daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();

      this.calDaysGrid.innerHTML = '';

      for (var p = 0; p < firstDayIndex; p++) {
        var emptyEl = document.createElement('div');
        emptyEl.className = 'room-booking-popup__cal-day room-booking-popup__cal-day--empty';
        this.calDaysGrid.appendChild(emptyEl);
      }

      var self = this;
      for (var d = 1; d <= daysInMonth; d++) {
        var dayDate = new Date(this.currentYear, this.currentMonth, d);
        dayDate.setHours(0, 0, 0, 0);
        var dateString = this.formatDate(dayDate);

        var dayBtn = document.createElement('button');
        dayBtn.type = 'button';
        dayBtn.className = 'room-booking-popup__cal-day font-family-avenir';
        dayBtn.textContent = d;
        dayBtn.dataset.date = dateString;

        if (dayDate < today) {
          dayBtn.classList.add('is-disabled');
          dayBtn.disabled = true;
        } else if (this.mockBookedDates.indexOf(dateString) !== -1) {
          dayBtn.classList.add('is-booked');
          dayBtn.disabled = true;
          dayBtn.title = 'Date Booked';
        } else {
          var isStart = (this.startDate === dateString);
          var isEnd = (this.endDate === dateString);

          if (isStart) {
            dayBtn.classList.add('is-selected', 'is-range-start');
            if (!this.endDate) {
              dayBtn.classList.add('is-single-selection');
            }
            dayBtn.title = 'Check-in: ' + this.formatShortDate(dateString);
          } else if (isEnd) {
            dayBtn.classList.add('is-selected', 'is-range-end');
            dayBtn.title = 'Check-out: ' + this.formatShortDate(dateString);
          } else if (this.startDate && this.endDate && dateString > this.startDate && dateString < this.endDate) {
            dayBtn.classList.add('is-in-range');
            dayBtn.title = 'Stay: ' + this.formatShortDate(dateString);
          } else if (this.startDate && !this.endDate && this.hoverDate && dateString > this.startDate && dateString < this.hoverDate) {
            dayBtn.classList.add('is-hover-range');
          } else if (this.startDate && !this.endDate && this.hoverDate && dateString === this.hoverDate) {
            dayBtn.classList.add('is-hover-end');
            dayBtn.title = 'Check-out: ' + this.formatShortDate(dateString);
          }

          dayBtn.addEventListener('click', (function(dateVal) {
            return function() {
              self.selectDate(dateVal);
            };
          })(dateString));

          dayBtn.addEventListener('mouseenter', (function(dateVal) {
            return function() {
              if (self.startDate && !self.endDate && dateVal > self.startDate) {
                self.hoverDate = dateVal;
                self.highlightHoverRange();
              }
            };
          })(dateString));
        }

        this.calDaysGrid.appendChild(dayBtn);
      }
    }

    selectDate(dateVal) {
      if (this.selectingStep === 'start' || (this.startDate && this.endDate)) {
        this.startDate = dateVal;
        this.endDate = null;
        this.hoverDate = null;
        this.selectingStep = 'end';
      } else if (this.selectingStep === 'end') {
        if (dateVal < this.startDate) {
          this.startDate = dateVal;
          this.endDate = null;
          this.hoverDate = null;
          this.selectingStep = 'end';
        } else if (dateVal === this.startDate) {
          this.showError('schedule_date', 'Check-out must be at least 1 day after Check-in');
          return;
        } else {
          if (this.rangeContainsBookedDate(this.startDate, dateVal)) {
            this.showError('schedule_date', 'Selected stay overlaps with booked dates. Please pick available dates.');
            return;
          }
          this.endDate = dateVal;
          this.hoverDate = null;
          this.selectingStep = 'start';
        }
      }

      this.updateDateRangeUI();
      this.renderCalendar();
    }

    updateOccupancyUI() {
      var checkedRadio = this.querySelector('input[name="occupancy_type"]:checked');
      var isTwo = checkedRadio && checkedRadio.value.indexOf('2') !== -1;

      this.segments.forEach(function(s) {
        var r = s.querySelector('input[type="radio"]');
        var isSelected = r ? r.checked : false;
        s.classList.toggle('is-selected', isSelected);
      });

      this.updatePriceSummary();
    }

    updatePriceSummary() {
      var checkedRadio = this.querySelector('input[name="occupancy_type"]:checked');
      var isTwo = checkedRadio && checkedRadio.value.indexOf('2') !== -1;
      var price = isTwo ? this.getPrice2() : this.getPrice1();
      if (this.summaryPrice && price) {
        this.summaryPrice.textContent = price;
      }
    }

    getActiveVariantId() {
      var checkedRadio = this.querySelector('input[name="occupancy_type"]:checked');
      var isTwo = checkedRadio && checkedRadio.value.indexOf('2') !== -1;
      return isTwo ? this.getVariant2() : this.getVariant1();
    }

    getActivePriceText() {
      var checkedRadio = this.querySelector('input[name="occupancy_type"]:checked');
      var isTwo = checkedRadio && checkedRadio.value.indexOf('2') !== -1;
      return isTwo ? this.getPrice2() : this.getPrice1();
    }

    open() {
      this.classList.add('is-active');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.renderCalendar();
      this.updateOccupancyUI();

      if (this.nameInput) {
        setTimeout(() => { this.nameInput.focus(); }, 100);
      }
    }

    close() {
      this.classList.remove('is-active');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.clearMessage();
    }

    onKeyDown(e) {
      if (e.key === 'Escape' && this.classList.contains('is-active')) {
        this.close();
      }
    }

    clearMessage() {
      if (this.messageEl) {
        this.messageEl.textContent = '';
        this.messageEl.className = 'room-booking-popup__message font-family-avenir';
        this.messageEl.style.display = 'none';
      }
    }

    showMessage(text, type) {
      if (!this.messageEl) return;
      this.messageEl.textContent = text;
      this.messageEl.className = 'room-booking-popup__message font-family-avenir is-' + (type || 'info');
      this.messageEl.style.display = 'block';
    }

    showError(fieldName, message) {
      var errEl = this.form.querySelector('[data-error="' + fieldName + '"]');
      if (errEl) {
        errEl.textContent = message;
        errEl.classList.add('is-visible');
      }
    }

    handleFormSubmit(e) {
      e.preventDefault();
      this.clearMessage();

      var name = (this.nameInput ? this.nameInput.value : '').trim();
      var email = (this.emailInput ? this.emailInput.value : '').trim();
      var checkedRadio = this.form.querySelector('input[name="occupancy_type"]:checked');
      var occupancy = checkedRadio ? checkedRadio.value : '1 Person';
      var priceText = this.getActivePriceText();

      var hasError = false;

      if (!name) {
        this.showError('guest_name', 'Enter guest full name');
        hasError = true;
      }

      var isPhoneValid = this.validatePhone(true);
      if (!isPhoneValid) {
        hasError = true;
      }

      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.showError('guest_email', 'Enter a valid email address');
        hasError = true;
      }

      if (!this.startDate) {
        this.showError('schedule_date', 'Select Check-in date on calendar');
        hasError = true;
      } else if (!this.endDate) {
        this.showError('schedule_date', 'Select Check-out date on calendar');
        hasError = true;
      }

      if (hasError) return;

      var fullPhone = (this.iti && typeof this.iti.getNumber === 'function')
        ? this.iti.getNumber()
        : (this.phoneInput ? this.phoneInput.value.trim() : '');

      var nights = this.getNightsCount();
      var stayDuration = nights + (nights === 1 ? ' Night' : ' Nights');
      var scheduleDateRange = this.startDate + ' to ' + this.endDate;

      this.setSubmitting(true);
      var self = this;

      // 1. Simultaneously send details to Supabase
      var bookingPayload = {
        guest_name: name,
        guest_phone: fullPhone,
        guest_email: email || '',
        check_in_date: this.startDate,
        check_out_date: this.endDate,
        schedule_date: scheduleDateRange,
        nights: nights,
        stay_duration: stayDuration,
        occupancy: occupancy,
        amount: priceText,
        currency: 'INR',
        status: 'pending_checkout',
        source: 'website_popup',
        created_at: new Date().toISOString()
      };

      if (this.supabaseUrl && this.supabaseKey) {
        var supabaseEndpoint = this.supabaseUrl.replace(/\/$/, '') + '/rest/v1/' + this.supabaseTable;
        fetch(supabaseEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': this.supabaseKey,
            'Authorization': 'Bearer ' + this.supabaseKey,
            'Prefer': 'return=minimal'
          },
          body: JSON.stringify(bookingPayload)
        }).catch(function(err) {
          console.warn('[Supabase Sync Notice]:', err);
        });
      } else {
        console.log('[Supabase Sync Payload Captured]:', bookingPayload);
      }

      // 2. Add to Shopify Cart:
      // Note: Properties prefixed with '_' are hidden from the customer in Checkout!
      var variantId = this.getActiveVariantId();
      var parsedVariantId = parseInt(variantId, 10);

      if (!variantId || isNaN(parsedVariantId)) {
        self.setSubmitting(false);
        self.showMessage(
          'Please assign your Room Booking products in Shopify Theme Customizer (Theme Settings > Room Booking Popup) to enable checkout.',
          'error'
        );
        return;
      }

      var properties = {
        '_Guest Name': name,
        '_Phone Number': fullPhone,
        '_Check-in': this.startDate,
        '_Check-out': this.endDate,
        '_Stay Duration': stayDuration,
        '_Occupancy': occupancy
      };
      if (email) {
        properties['_Guest Email'] = email;
      }

      var lineItemPayload = {
        id: parsedVariantId,
        quantity: 1,
        properties: properties
      };

      // Clear any previous booking items first so only exactly ONE booking is checked out
      fetch('/cart/clear.js', { method: 'POST' })
        .then(function() {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(lineItemPayload)
          });
        })
        .then(function(response) {
          return response.json().then(function(data) {
            if (!response.ok) {
              throw new Error(data.description || data.message || 'Unable to proceed to checkout.');
            }
            return data;
          });
        })
        .then(function() {
          window.location.href = '/checkout';
        })
        .catch(function(err) {
          self.setSubmitting(false);
          self.showMessage(err.message || 'Could not reach checkout. Please try again.', 'error');
        });
    }

    setSubmitting(isLoading) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = isLoading;
      if (this.submitSpinner) {
        this.submitSpinner.style.display = isLoading ? 'inline-block' : 'none';
      }
      if (this.submitText) {
        this.submitText.style.display = isLoading ? 'none' : 'inline-block';
      }
    }
  }

  if (!customElements.get('room-booking-popup')) {
    customElements.define('room-booking-popup', RoomBookingPopup);
  }

  // Global trigger listener
  document.addEventListener('click', function(e) {
    var trigger = e.target.closest('.js-room-booking-trigger');
    if (trigger) {
      e.preventDefault();
      var popup = document.querySelector('room-booking-popup');
      if (popup && typeof popup.open === 'function') {
        popup.open();
      }
    }
  });
})();
